/**
 * Lot search — combobox autocomplete over the user's existing inventory.
 * Used by Sale / Trade / Grading / Rip forms to pick a source lot for an item.
 *
 * Each option carries { id, label, type, basis, fmv, qty, product? } so the
 * caller's `onSelect` can populate fields downstream.
 */
import { DB } from "../lib/db";
import { BULK_CLASSES, bulkPoolLotId } from "../lib/bulk";
import { escapeHtml } from "../lib/dom";
import { cardLockReason } from "../lib/lotLock";

export interface LotOption {
  id: string;
  label: string;
  type: "Card" | "Sealed" | "Trade" | "Purchase";
  qty: number;
  basis: number;
  fmv: number;
  product?: string;
  sortKey: string;
}

export interface LotSearchOptions {
  kind?: "card" | "sealed" | "any";
  excludeIds?: () => Set<string>;
  /** When true, card lots currently out for grading/repair are omitted. */
  excludeLocked?: boolean;
  /** When true, also list trades and purchases as pickable "deals". */
  includeDeals?: boolean;
  onSelect?: (lot: LotOption) => void;
}

export function wireLotSearch(wrapper: HTMLElement, opts: LotSearchOptions): void {
  const input = wrapper.querySelector<HTMLInputElement>("input")!;
  const list = wrapper.querySelector<HTMLElement>(".combobox-list")!;
  let activeIdx = -1;
  const kind = opts.kind || "any";

  const buildLots = (): LotOption[] => {
    const out: LotOption[] = [];
    if (kind === "card" || kind === "any") {
      // Bulk pools are containers, not lots — they must never be picked as a
      // normal line item (found 2026-07-26: a pool chosen via this search took
      // the normal-lot path in a trade, stomping the pool row's status).
      // Every flow that moves bulk has its own + Bulk button.
      const poolIds = new Set<string>(BULK_CLASSES.map(k => bulkPoolLotId(k)));
      DB.table("cards").filter(r => (Number(r["Qty"]) || 0) > 0 && !poolIds.has(String(r["Card Lot ID"] || "")) && !(opts.excludeLocked && cardLockReason(r["Card Lot ID"]))).forEach(r => out.push({
        id: r["Card Lot ID"],
        label: `${r["Card Lot ID"]} · ${r["Card Name"] || ""}${r["Set"] ? ` · ${r["Set"]}` : ""}${r["Card #"] ? ` #${r["Card #"]}` : ""}${r["Condition/Grade"] ? ` (${r["Condition/Grade"]})` : ""} — qty ${r["Qty"]}`,
        sortKey: (r["Card Name"] || "") + " " + (r["Card Lot ID"] || ""),
        type: "Card",
        qty: Number(r["Qty"]) || 1,
        basis: (Number(r["Total Basis"]) || 0) / Math.max(Number(r["Qty"]) || 1, 1),
        fmv:   (Number(r["Current FMV"]) || 0) / Math.max(Number(r["Qty"]) || 1, 1),
      }));
    }
    if (kind === "sealed" || kind === "any") {
      DB.table("sealed").filter(r => (Number(r["Qty"]) || 0) > 0).forEach(r => out.push({
        id: r["Lot ID"],
        label: `${r["Lot ID"]} · ${r["Product"] || ""}${r["Set"] ? ` · ${r["Set"]}` : ""} — qty ${r["Qty"]}`,
        sortKey: (r["Product"] || "") + " " + (r["Lot ID"] || ""),
        type: "Sealed",
        qty: Number(r["Qty"]) || 1,
        basis: Number(r["Unit Basis"]) || 0,
        fmv:   (Number(r["Current FMV"]) || 0) / Math.max(Number(r["Qty"]) || 1, 1),
        product: r["Product"] || "",
      }));
    }
    if (opts.includeDeals) {
      DB.table("trades").forEach(r => out.push({
        id: r["Trade ID"],
        label: `${r["Trade ID"]} · Trade${r["Date"] ? " · " + r["Date"] : ""}`,
        sortKey: "~1 " + (r["Trade ID"] || ""),
        type: "Trade", qty: 0, basis: 0, fmv: 0,
      }));
      DB.table("purchases").forEach(r => out.push({
        id: r["Purchase ID"],
        label: `${r["Purchase ID"]} · Purchase${r["Vendor"] ? " · " + r["Vendor"] : ""}${r["Date"] ? " · " + r["Date"] : ""}`,
        sortKey: "~2 " + (r["Purchase ID"] || ""),
        type: "Purchase", qty: 0, basis: 0, fmv: 0,
      }));
    }
    return out.sort((a, b) => a.sortKey.localeCompare(b.sortKey));
  };

  let matches: LotOption[] = [];

  const renderList = (filter: string) => {
    let all = buildLots();
    if (opts.excludeIds) {
      const excluded = opts.excludeIds();
      if (excluded?.size) all = all.filter(x => !excluded.has(x.id));
    }
    const f = (filter || "").toLowerCase().trim();
    matches = (f
      ? all.filter(x => x.label.toLowerCase().includes(f) || x.id.toLowerCase().includes(f))
      : all
    ).slice(0, 60);
    if (!matches.length) {
      const hiddenHint = opts.excludeIds && opts.excludeIds().size ? " (already-picked lots are hidden)" : "";
      list.innerHTML = `<div class="combobox-empty">No matching lots${hiddenHint}.</div>`;
    } else {
      list.innerHTML = matches.map((x, i) =>
        `<div class="combobox-item${i === activeIdx ? ' active' : ''}" data-id="${escapeHtml(x.id)}">${escapeHtml(x.label)}</div>`
      ).join("");
    }
    list.style.display = "block";
  };
  const hideList = () => { list.style.display = "none"; activeIdx = -1; };

  input.addEventListener("focus", () => { activeIdx = -1; });
  input.addEventListener("input", () => {
    activeIdx = -1;
    if (input.value && input.value.trim().length > 0) renderList(input.value);
    else hideList();
  });
  input.addEventListener("blur", () => setTimeout(hideList, 180));
  input.addEventListener("keydown", (e) => {
    const isOpen = list.style.display === "block";
    if (e.key === "ArrowDown") {
      e.preventDefault();
      if (!isOpen) { renderList(input.value); return; }
      const items = list.querySelectorAll(".combobox-item");
      activeIdx = Math.min(activeIdx + 1, items.length - 1);
      renderList(input.value);
      list.querySelector(".combobox-item.active")?.scrollIntoView({ block: "nearest" });
    } else if (e.key === "ArrowUp") {
      if (!isOpen) return;
      e.preventDefault();
      activeIdx = Math.max(activeIdx - 1, 0);
      renderList(input.value);
    } else if (e.key === "Enter") {
      if (isOpen && activeIdx >= 0 && matches[activeIdx]) {
        e.preventDefault();
        const lot = matches[activeIdx];
        hideList();
        opts.onSelect?.(lot);
      }
    } else if (e.key === "Escape") hideList();
  });
  list.addEventListener("mousedown", (e) => {
    const item = (e.target as HTMLElement).closest<HTMLElement>(".combobox-item");
    if (!item) return;
    e.preventDefault();
    const id = item.dataset.id;
    const lot = matches.find(x => x.id === id);
    hideList();
    if (lot) opts.onSelect?.(lot);
  });
}
